import { Icon } from '@/features/common/Icon';
import {
  MONTH_NAMES,
  WEEKDAY_NAMES,
  formatIsoDay,
  monthGrid,
  todayIso,
} from '@/features/common/calendar';
import { END_LABEL, dayClasses } from '@/features/common/DateRangeFilter/dateRange';
import type { DateRangeCalendarProps } from '@/features/common/DateRangeFilter/types/DateRangeCalendarProps';
import { useLanguage } from '@/i18n/LanguageContext';
import { dateRangeFilter } from '@/i18n/dictionaries/dateRangeFilter';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * MUD's date picker (Components/input-date-picker.html) as DateRangeFilter opens it, in MUD's own
 * markup: `.date-picker[role=dialog]` with its header, weekdays and days. Each day shows where it
 * stands against the period (dayClasses); picking one fills the active end.
 */
export function DateRangeCalendar({
  id,
  active,
  view,
  from,
  to,
  onShiftMonth,
  onPick,
}: DateRangeCalendarProps) {
  const { t } = useTranslation(dateRangeFilter);
  const { language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'ro';
  const today = todayIso();
  return (
    <div
      className="date-picker"
      role="dialog"
      id={id}
      aria-label={`${t('calendar')}: ${t(END_LABEL[active])}`}
    >
      <div className="date-picker__header">
        <button
          type="button"
          className="date-picker__nav"
          aria-label={t('previousMonth')}
          onClick={() => onShiftMonth(-1)}
        >
          <Icon name="chevron-left" className="date-range-filter__nav-icon" />
        </button>
        <div className="date-picker__month" aria-live="polite">
          {MONTH_NAMES[lang][view.month]} {view.year}
        </div>
        <button
          type="button"
          className="date-picker__nav"
          aria-label={t('nextMonth')}
          onClick={() => onShiftMonth(1)}
        >
          <Icon name="chevron-right" className="date-range-filter__nav-icon" />
        </button>
      </div>
      <div className="date-picker__grid date-picker__grid--days">
        <div className="date-picker__weekdays" aria-hidden="true">
          {WEEKDAY_NAMES[lang].map((day) => (
            <div className="date-picker__weekday" key={day}>
              {day}
            </div>
          ))}
        </div>
        <div className="date-picker__days">
          {monthGrid(view.year, view.month).map((cell) => (
            <button
              key={cell.iso}
              type="button"
              className={`${dayClasses(cell.iso, { from, to, today })}${cell.outside ? ' is-outside' : ''}`}
              disabled={cell.outside}
              aria-label={formatIsoDay(cell.iso)}
              aria-pressed={cell.iso === from || cell.iso === to}
              onClick={() => onPick(cell.iso)}
            >
              {cell.day}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
